import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Box } from "@mui/material";
import Flex from "../../../../../components/Flex";
import Text from "../../../../../components/Text";
import useDisclosure from "../../../../../hooks/use-disclosure";
import DocumentPreviewModal from "../chat/DocumentPreviewModal";
import { stripMarkup } from "../../../../../utils/sanitize-html";

interface ChatBubbleProps {
  message: string;
  isSender: boolean;
  color?: string;
  attachment?: string | null;
  attachmentExt?: "file" | "image" | null;
  fileName?: string | null;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  isSender,
  color,
  attachment,
  attachmentExt,
  fileName,
}) => {
  const isImage = attachmentExt === "image" && attachment;
  const isFile = attachmentExt === "file" && attachment;
  const previewDisclosure = useDisclosure();

  const handlePreview = () => {
    if (attachment) {
      // previewDisclosure.open();
      window.open(attachment, "_blank");
    }
  };

  return (
    <>
      <Flex
        flex={1}
        flexDirection="column"
        gap={1}
        sx={{
          borderRadius: 2,
          borderTopLeftRadius: isSender ? 2 : undefined,
          borderTopRightRadius: isSender ? undefined : 2,
          bgcolor: color || (isSender ? color : "grey.300"),
          color: isSender ? "black" : "black",
          position: "relative",
          p: 1.2,
          mt: 1,
          maxWidth: "100%",
        }}
      >
        {isImage && attachment && (
          <Box
            component="img"
            src={attachment}
            alt="Chat attachment"
            onClick={handlePreview}
            sx={{
              maxWidth: "100%",
              maxHeight: "300px",
              objectFit: "contain",
              borderRadius: 1,
              cursor: "pointer",
              border: "1px solid",
              borderColor: "grey.300",
              bgcolor: "grey.50",
            }}
          />
        )}

        {isFile && attachment && (
          <Flex
            alignItems="center"
            gap={1}
            p={1.5}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.5)",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.300",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.7)",
              },
            }}
            onClick={handlePreview}
          >
            <InsertDriveFileIcon sx={{ color: "primary.main", fontSize: 28 }} />
            <Flex flexDirection="column" flex={1} minWidth={0}>
              <Text fontSize="13px" fontWeight={500}>
                {fileName}
              </Text>
              <Text fontSize="11px" color="text.secondary">
                Click to preview
              </Text>
            </Flex>
            <DownloadIcon sx={{ color: "primary.main", fontSize: 20 }} />
          </Flex>
        )}

        {message &&
          (message.includes("<") && message.includes(">") ? (
            <Box
              sx={{
                wordBreak: "break-word",
                // Preserve newlines from the original message as line breaks
                // (stripMarkup keeps the text but the container would otherwise
                // collapse the `\n` whitespace).
                whiteSpace: "pre-wrap",
                color: "text.primary",
                fontSize: "inherit",
                "& p": { margin: 0 },
                "& img": { maxWidth: "100%" },
              }}
              // Render with the safe-formatting whitelist: any <a>/<script>
              // and all attributes (href/on*) are stripped, so a stored
              // payload can never render a live link that leaks the host via
              // the Referer header (the SSRF/referrer-leak finding).
              dangerouslySetInnerHTML={{ __html: stripMarkup(message) }}
            />
          ) : (
            <Text
              color={"text.primary"}
              sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
            >
              {message}
            </Text>
          ))}
      </Flex>

      {attachment && (
        <DocumentPreviewModal
          file={attachment}
          fileName={fileName || undefined}
          fileType={attachmentExt || "file"}
          disclosureHost={previewDisclosure}
        />
      )}
    </>
  );
};

export default ChatBubble;
